import React, { useState, useEffect } from 'react';
import { Card, Typography, Space, Spin } from 'antd';
import { 
  UserOutlined, 
  DollarOutlined, 
  TeamOutlined,
  RiseOutlined 
} from '@ant-design/icons';
import { useUser } from '../../contexts/UserContext';
import { apiClient } from '../../services/apiClient';
import { ValueMetricDisplay, ValueType } from '../Shared/ValueMetricDisplay';

const { Text } = Typography;

/**
 * Standard default metrics for consistent display
 * Used when no data is available or during development
 */
const DEFAULT_METRICS: ValueMetrics = {
  customer: { ranking: 7, ratio: 4.5 },
  business: { ranking: 6, ratio: 1.15 },
  stakeholder: { ranking: 8, ratio: 2.4 }
};

interface ValueMetrics {
  customer: { ranking: number; ratio: number };
  business: { ranking: number; ratio: number };
  stakeholder: { ranking: number; ratio: number };
}

interface ValueGeneratorProps {
  metrics?: ValueMetrics;
  compact?: boolean;
}

export const ValueGenerator: React.FC<ValueGeneratorProps> = ({ 
  metrics: providedMetrics,
  compact = false 
}) => {
  const { selectedCompany } = useUser();
  const [metrics, setMetrics] = useState<ValueMetrics | null>(providedMetrics || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (providedMetrics) {
      setMetrics(providedMetrics);
    } else if (selectedCompany?.id) {
      void loadMetrics();
    } else {
      setMetrics(DEFAULT_METRICS);
    }
  }, [selectedCompany?.id, providedMetrics]);

  const loadMetrics = async () => {
    if (!selectedCompany?.id) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Try to fetch from company value metrics endpoint
      const url = `/company-models/${selectedCompany.id}/value-metrics`;
      const params = { identifier_type: 'company_id' };
      
      const response = await apiClient.get(url, { params });
      const data = response.data;
      
      // Map the response to our ValueMetrics format
      if (data) {
        setMetrics({
          customer: {
            ranking: data.customer_value?.ranking || data.customer_value?.score || DEFAULT_METRICS.customer.ranking,
            ratio: data.customer_value?.ratio || DEFAULT_METRICS.customer.ratio
          },
          business: {
            ranking: data.business_value?.ranking || data.business_value?.score || DEFAULT_METRICS.business.ranking,
            ratio: data.business_value?.ratio || DEFAULT_METRICS.business.ratio
          },
          stakeholder: {
            ranking: data.stakeholder_value?.ranking || data.stakeholder_value?.score || DEFAULT_METRICS.stakeholder.ranking,
            ratio: data.stakeholder_value?.ratio || DEFAULT_METRICS.stakeholder.ratio
          }
        });
      }
    } catch (err: unknown) {
      // Common: 400 when company has no artifact tree yet, or network/CORS — we fall back to mock/defaults.
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console -- intentional diagnostics for missing backend data
        console.debug('[ValueGenerator] value-metrics unavailable, using fallback', err);
      }
      const detail = (() => {
        if (err && typeof err === 'object' && 'response' in err) {
          const d = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
          if (typeof d === 'string') return d;
          if (Array.isArray(d)) return d.map((x) => String(x)).join('; ');
        }
        return 'Failed to load metrics';
      })();
      setError(detail);
      setMetrics(DEFAULT_METRICS);
    } finally {
      setLoading(false);
    }
  };

  if (!metrics) {
    if (loading) {
      return compact ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Spin size="small" />
          <Text style={{ fontSize: '11px', color: '#666' }}>Loading...</Text>
        </div>
      ) : (
        <Card size="small" style={{ minWidth: '300px' }}>
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Spin size="small" />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Loading metrics...</Text>
            </div>
          </div>
        </Card>
      );
    }
    return null;
  }

  if (compact) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '16px',
        padding: '8px 12px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        {/* Customer */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <UserOutlined style={{ color: '#3b82f6', fontSize: '12px' }} />
            <Text style={{ fontSize: '11px', fontWeight: '500' }}>Customer</Text>
          </div>
          <ValueMetricDisplay
            valueRatio={metrics.customer.ratio}
            assessmentRank={metrics.customer.ranking}
            valueType="customer"
            variant="compact"
            showLabels={false}
          />
        </div>
        
        {/* Business */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <DollarOutlined style={{ color: '#10b981', fontSize: '12px' }} />
            <Text style={{ fontSize: '11px', fontWeight: '500' }}>Business</Text>
          </div>
          <ValueMetricDisplay
            valueRatio={metrics.business.ratio}
            assessmentRank={metrics.business.ranking}
            valueType="business"
            variant="compact"
            showLabels={false}
          />
        </div>
        
        {/* Stakeholder */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <TeamOutlined style={{ color: '#8b5cf6', fontSize: '12px' }} />
            <Text style={{ fontSize: '11px', fontWeight: '500' }}>Stakeholder</Text>
          </div>
          <ValueMetricDisplay
            valueRatio={metrics.stakeholder.ratio}
            assessmentRank={metrics.stakeholder.ranking}
            valueType="stakeholder"
            variant="compact"
            showLabels={false}
          />
        </div>
      </div>
    );
  }

  return (
    <Card 
      size="small" 
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <RiseOutlined />
          <Text strong>Value Generator</Text>
        </div>
      }
      style={{ 
        minWidth: '320px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef'
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Customer Value */}
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '8px' 
          }}>
            <UserOutlined style={{ color: '#3b82f6', fontSize: '16px' }} />
            <Text style={{ fontSize: '13px', fontWeight: '600' }}>Customer Value</Text>
          </div>
          <ValueMetricDisplay
            valueRatio={metrics.customer.ratio}
            assessmentRank={metrics.customer.ranking}
            valueType="customer"
            variant="standard"
            showLabels={true}
          />
        </div>

        {/* Business Value */}
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '8px' 
          }}>
            <DollarOutlined style={{ color: '#10b981', fontSize: '16px' }} />
            <Text style={{ fontSize: '13px', fontWeight: '600' }}>Business Value</Text>
          </div>
          <ValueMetricDisplay
            valueRatio={metrics.business.ratio}
            assessmentRank={metrics.business.ranking}
            valueType="business"
            variant="standard"
            showLabels={true}
          />
        </div>

        {/* Stakeholder Value */}
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '8px' 
          }}>
            <TeamOutlined style={{ color: '#8b5cf6', fontSize: '16px' }} />
            <Text style={{ fontSize: '13px', fontWeight: '600' }}>Stakeholder Value</Text>
          </div>
          <ValueMetricDisplay
            valueRatio={metrics.stakeholder.ratio}
            assessmentRank={metrics.stakeholder.ranking}
            valueType="stakeholder"
            variant="standard"
            showLabels={true}
          />
        </div>
      </Space>
    </Card>
  );
};
